import { clockIn, dayKey, RANGES, shortStamp, spanLabel, type Zone } from './format';

// A time range: a preset that follows the clock (the last hour, anchored when the view last refreshed), or two fixed ends.
export type TimeRange = { kind: 'preset'; key: string } | { kind: 'custom'; from: number; to: number; label?: string };

export const DEFAULT_RANGE = { kind: 'preset', key: '1h' } as const satisfies TimeRange;
const MIN_SPAN_MS = 10_000;
const MAX_SPAN_MS = 31 * 24 * 3600_000;

export const presetOf = (key: string): (typeof RANGES)[number] =>
    RANGES.find((preset) => preset.key === key) ?? (RANGES[2] as (typeof RANGES)[number]);

export function resolveRange(range: TimeRange, anchor: number): { from: number; to: number } {
    return range.kind === 'preset' ? { from: anchor - presetOf(range.key).ms, to: anchor } : { from: range.from, to: range.to };
}

export function rangeLabel(range: TimeRange, zone: Zone): string {
    if (range.kind === 'preset') {
        return presetOf(range.key).label;
    }

    return range.label ?? stampRange(range.from, range.to, zone);
}

// `14 Oct 00:14 → 00:34`, the day once when both ends share it.
export function stampRange(from: number, to: number, zone: Zone): string {
    const end = dayKey(from, zone) === dayKey(to, zone) ? clockIn(new Date(to), zone).slice(0, 5) : shortStamp(to, zone);

    return `${shortStamp(from, zone)} → ${end}`;
}

// What the volume panel names the range by: the preset, the label it was given, or its span.
export function rangeTitle(range: TimeRange): string {
    if (range.kind === 'preset') {
        return presetOf(range.key).label;
    }

    return range.label ?? spanLabel(range.to - range.from);
}

// The range a step wider (`[`): the next preset, or twice the span around the same middle, never past now.
export function zoomOut(range: TimeRange, now: number): TimeRange {
    if (range.kind === 'preset') {
        const index = RANGES.findIndex((preset) => preset.key === range.key);
        const next = RANGES[Math.min(RANGES.length - 1, index + 1)];

        return next ? { kind: 'preset', key: next.key } : range;
    }
    const span = Math.min(MAX_SPAN_MS, (range.to - range.from) * 2);
    const middle = (range.from + range.to) / 2;
    const to = Math.min(now, middle + span / 2);

    return { kind: 'custom', from: Math.round(to - span), to: Math.round(to) };
}

// Half the span around the middle (`]`).
export function zoomIn(range: TimeRange, anchor: number): TimeRange {
    const { from, to } = resolveRange(range, anchor);
    const span = Math.max(MIN_SPAN_MS, (to - from) / 2);
    const middle = (from + to) / 2;

    return { kind: 'custom', from: Math.round(middle - span / 2), to: Math.round(middle + span / 2) };
}

// Further back, when the list reaches the start of the range: the next preset, or the same span once more.
export function extendBack(range: TimeRange, anchor: number): { range: TimeRange; label: string } | null {
    if (range.kind === 'preset') {
        const index = RANGES.findIndex((preset) => preset.key === range.key);
        const next = RANGES[index + 1];

        return next ? { range: { kind: 'preset', key: next.key }, label: `Search the ${next.label.toLowerCase()}` } : null;
    }
    const { from, to } = resolveRange(range, anchor);
    const span = to - from;
    if (to - (from - span) > MAX_SPAN_MS) {
        return null;
    }

    return { range: { kind: 'custom', from: from - span, to }, label: `Search ${spanLabel(span)} further back` };
}

export const clampRange = (from: number, to: number): { from: number; to: number } => {
    const end = Math.max(from + MIN_SPAN_MS, to);

    return { from: Math.max(end - MAX_SPAN_MS, from), to: end };
};

// The URL: `range=4h` for a preset (1 h is the default and left out), `from` and `to` in ms for fixed ends.
export function rangeFromParams(params: URLSearchParams): TimeRange {
    const from = Number(params.get('from'));
    const to = Number(params.get('to'));
    if (from > 0 && to > from) {
        const clamped = clampRange(from, to);

        return { kind: 'custom', ...clamped };
    }
    const key = params.get('range');

    return key && RANGES.some((preset) => preset.key === key) ? { kind: 'preset', key } : DEFAULT_RANGE;
}

export function rangeToParams(params: URLSearchParams, range: TimeRange): void {
    if (range.kind === 'custom') {
        params.set('from', String(range.from));
        params.set('to', String(range.to));
    } else if (range.key !== DEFAULT_RANGE.key) {
        params.set('range', range.key);
    }
}

// A wall-clock time in Rome (or UTC), as typed: `2026-10-14 09:30` or `2026-10-14 09:30:15`.
const STAMP = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/;

function romeOffset(ms: number): number {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Europe/Rome',
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(new Date(ms));
    const get = (type: string): number => Number(parts.find((part) => part.type === type)?.value);
    const wall = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));

    return wall - Math.floor(ms / 1000) * 1000;
}

export function parseStamp(text: string, zone: Zone): number | null {
    const match = STAMP.exec(text.trim());
    if (!match) {
        return null;
    }
    const [, y, mo, d, h, mi, s] = match.map(Number) as number[];
    const wall = Date.UTC(y as number, (mo as number) - 1, d, h, mi, s || 0);
    if (Number.isNaN(wall)) {
        return null;
    }
    if (zone === 'utc') {
        return wall;
    }
    // Twice: the offset at the guess, then at the answer, so the hour around a DST change lands right.
    const guess = wall - romeOffset(wall);

    return wall - romeOffset(guess);
}

export function formatStamp(ms: number, zone: Zone): string {
    const date = new Date(zone === 'utc' ? ms : ms + romeOffset(ms));
    const pad = (n: number): string => String(n).padStart(2, '0');

    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
}
